import React, {useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';

import styles from './TreeMapZ.module.scss';

export const TreeMapZ = ({width, height, data, tile, sumFunc, color}) => {
  const chartRef = useRef();

  useEffect(() => {
    const element = chartRef.current;

    const w = width,
      h = height,
      x = d3.scaleLinear().range([0, w]),
      y = d3.scaleLinear().range([0, h]);
    let root,
      node;

    const treemap = data => d3.treemap()
      .round(false)
      .size([w, h])
      .tile(d3[tile])(d3.hierarchy(data)
        .sum(d => sumFunc ? (sumFunc(d.size) || d.size) : d.size)
        .sort((a, b) => b.value - a.value));

    const svg = d3.select(element).append('div')
      .attr('class', `${styles.chart}`)
      .style('width', w + 'px')
      .style('height', h + 'px')
      .append('svg:svg')
      .attr('width', w)
      .attr('height', h)
      .append('svg:g')
      .attr('transform', 'translate(.5,.5)');

    node = root = treemap(data);

    const nodes = root.descendants()
      .filter(function(d) {return !d.children;});

    const zoom = d => {
      const kx = w / (d.x1 - d.x0), ky = h / (d.y1 - d.y0);
      x.domain([d.x0, d.x0 + (d.x1 - d.x0)]);
      y.domain([d.y0, d.y0 + (d.y1 - d.y0)]);

      const t = svg.selectAll('g.cell').transition()
        .duration(d3.event.altKey ? 7500 : 750)
        .attr('transform', function(d) {return 'translate(' + x(d.x0) + ',' + y(d.y0) + ')';});

      t.select('rect')
        .attr('width', function(d) {return kx * (d.x1 - d.x0) - 1;})
        .attr('height', function(d) {return ky * (d.y1 - d.y0) - 1;});

      t.select('text')
        .attr('x', function(d) {return kx * (d.x1 - d.x0) / 2;})
        .attr('y', function(d) {return ky * (d.y1 - d.y0) / 2;})
        .style('opacity', function(d) {return kx * (d.x1 - d.x0) > d.w ? 1 : 0;});

      node = d;
      d3.event.stopPropagation();
    };

    const cell = svg.selectAll('g')
      .data(nodes)
      .enter().append('svg:g')
      .attr('class', 'cell')
      .attr('transform', function(d) {return 'translate(' + d.x0 + ',' + d.y0 + ')';})
      .on('click', function(d) {return zoom(node === d.parent ? root : d.parent);});

    cell.append('svg:rect')
      .attr('width', function(d) {return (d.x1 - d.x0) - 1;})
      .attr('height', function(d) {return (d.y1 - d.y0) - 1;})
      .style('fill', function(d) {return color(d.parent.data.name);});

    cell.append('svg:text')
      .attr('x', function(d) {return (d.x1 - d.x0) / 2;})
      .attr('y', function(d) {return (d.y1 - d.y0) / 2;})
      .attr('dy', '.35em')
      .attr('text-anchor', 'middle')
      .text(function(d) {return d.data.name;})
      // eslint-disable-next-line no-invalid-this
      .style('opacity', function(d) {d.w = this.getComputedTextLength(); return (d.x1 - d.x0) > d.w ? 1 : 0;});

    d3.select(window).on('click', function() {zoom(root);});

    return () => d3.select(element).selectAll('*').remove();
  }, [width, height, data, tile, sumFunc, color]);

  return <div className={styles.container} ref={chartRef} />;
};

TreeMapZ.propTypes = {
  color: PropTypes.func,
  data: PropTypes.object.isRequired,
  height: PropTypes.number,
  sumFunc: PropTypes.func,
  tile: PropTypes.string,
  width: PropTypes.number,
};

TreeMapZ.defaultProps = {
  color: d3.scaleOrdinal(d3.schemeCategory10),
  height: 500,
  tile: 'treemapResquarify',
  width: 960,
};
